'use client'

import { useEffect, useState } from "react"
import { Collapse } from "@/components/ui/Collapse"
import { getName, getStaffByRole } from "@/services/aroeven"
import { parsePhone } from "@/services/text"
import Link from "next/link"
import { object, z } from 'zod'
import { messages } from '@/services/validation'
import { executeRecaptcha, useRecaptcha } from "@/services/recaptcha/client"
import { status as s } from '@/services/status'
import { postContactAccomodation, postContactSchoolStay } from "@/api/actions"
import Loading from "app/loading"

const ContactSchema = z.object({
    name: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    jobTitle: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    establishment: z.string().trim().min(1, messages.required).max(255, messages.tooLong),
    email: z.string().trim().min(1, messages.required).email(messages.email),
    phone: z.string().trim().min(1, messages.required).min(10, messages.tooShort).max(255, messages.tooLong),
    message: z.string().trim().min(1, messages.required).max(10000, messages.tooLong),
})

type BookingSidebarProps = {
    accomodation: Accomodation,
    proposedBy: Aroeven & AroevenStaffs
}

export default function BookingSidebar({
    accomodation,
    proposedBy
}: BookingSidebarProps) {

    const [contactCollapseOpen, setContactCollapseOpen] = useState<boolean>(false)

    const [status, setStatus] = useState<string>(s.idle)
    useRecaptcha(contactCollapseOpen)

    const [errors, setErrors] = useState([])
    const [contact, setContact] = useState({
        name: '',
        jobTitle: '',
        establishment: '',
        email: '',
        phone: '',
        message: '',
        level: '',
        headCount: ''
    })

    async function postContact() {
        setErrors([])
        try {
            ContactSchema.parse(contact)
        }
        catch (error) {
            setErrors(error.issues)
            return
        }

        setStatus(s.loading)
        try {
            const token = await executeRecaptcha('contact_school_stay')
            await postContactSchoolStay(accomodation.proposedBy, {
                ...contact,
                object: accomodation
            }, token)
            setStatus(s.finished)
        }
        catch (error) {
            console.log(error)
            setStatus(s.error)
            return
        }
    }

    return (
        <>
            {status === s.loading &&
                <Loading />
            }
            <div className="d-flex flex-column flex-md-row flex-lg-column justify-end" data-offset={450}>
                <div className="encart-2 ml-lg-0 px-30 py-30 bg-bloc w-360">
                    <div className="row y-gap-15 items-center justify-between">
                        <div className="col-auto">
                            <div className="text-14 text-light-1">
                                <h4>Proposé par :</h4>
                                <h4>
                                    <a className="link">
                                        {getName(proposedBy)} <i className="fi fi-ss-angle-circle-right lh-15" />
                                    </a>
                                </h4>
                            </div>
                        </div>
                        <div className="col-auto text-ocre-dark numbers text-18">
                            <i className="fi fi-rr-phone-flip" /> Contactez {getStaffByRole(proposedBy, 'school')?.firstName ?? 'l\'Aroéven'} au {parsePhone(getStaffByRole(proposedBy, 'school')?.phone ?? proposedBy.phone)}
                        </div>
                    </div>
                    <div className="separateur d-flex flex-row justify-content-between">
                        <div className="divider" />
                        <div>ou</div>
                        <div className="divider" />
                    </div>
                    <div className="row y-gap-20">
                        <div className="col-12">
                            <div className="col-12 mt-5">
                                <div className="accordion -simple js-accordion">
                                    <div className="col-12">
                                        <div className={"accordion__item mb-15 " + (contactCollapseOpen === true ? 'is-active' : '')}>
                                            <a className="accordion__button button-primary demande mb-10 d-flex items-center justify-content-center" data-bs-toggle='collapse' href={'#sa' + accomodation.id + '-contact-collapse-content'} role="button">
                                                <div className="accordion__icon size-20 flex-center mr-10">
                                                    <i className="fi fi-bs-arrow-small-down" />
                                                    <i className="fi fi-bs-arrow-small-up" />
                                                </div>
                                                <div>
                                                    Complétez la demande de contact
                                                </div>
                                            </a>
                                            <Collapse
                                                id={'sa' + accomodation.id + '-contact-collapse-content'}
                                                className='mt-10 y-gap-20'
                                                onShow={() => {
                                                    setContactCollapseOpen(true)
                                                }}
                                                onHide={() => {
                                                    setContactCollapseOpen(false)
                                                }}
                                            >
                                                {status === s.finished &&
                                                    <>
                                                        <section className="texte">
                                                            <div className="container">
                                                                <div className="alert alert-success" role="alert">
                                                                    <h4 className="alert-heading">Merci !</h4>
                                                                    <p>Votre demande a bien été envoyée.</p>
                                                                </div>
                                                            </div>
                                                        </section>
                                                    </>
                                                }
                                                {[s.idle, s.error].includes(status) === true &&
                                                    <>
                                                        {status === s.error &&
                                                            <div className="alert alert-danger" role="alert">
                                                                <h4 className="alert-heading">Une erreur est survenue.</h4>
                                                                <p>Veuillez vérifier les données saisies dans le formulaire ou réessayer plus tard.</p>
                                                            </div>
                                                        }
                                                        <div className="form-input">
                                                            <input type="text" className={errors.filter(e => e.path.join('-') === 'name').length > 0 ? 'requis' : ''} value={contact.name} onChange={(e) => setContact({
                                                                ...contact,
                                                                name: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Nom <span className="obligatoire">*</span>
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'name').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'name')[0].message}</div>}
                                                        <div className="form-input">
                                                            <input type="text" className={errors.filter(e => e.path.join('-') === 'jobTitle').length > 0 ? 'requis' : ''} value={contact.jobTitle} onChange={(e) => setContact({
                                                                ...contact,
                                                                jobTitle: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Fonction <span className="obligatoire">*</span>
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'jobTitle').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'jobTitle')[0].message}</div>}
                                                        <div className="form-input">
                                                            <input type="text" className={errors.filter(e => e.path.join('-') === 'establishment').length > 0 ? 'requis' : ''} value={contact.establishment} onChange={(e) => setContact({
                                                                ...contact,
                                                                establishment: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Etablissement / structure <span className="obligatoire">*</span>
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'establishment').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'establishment')[0].message}</div>}
                                                        <div className="form-input">
                                                            <input type="text" className={errors.filter(e => e.path.join('-') === 'email').length > 0 ? 'requis' : ''} value={contact.email} onChange={(e) => setContact({
                                                                ...contact,
                                                                email: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Adresse email <span className="obligatoire">*</span>
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'email').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'email')[0].message}</div>}
                                                        <div className="form-input">
                                                            <input type="text" className={errors.filter(e => e.path.join('-') === 'phone').length > 0 ? 'requis' : ''} value={contact.phone} onChange={(e) => setContact({
                                                                ...contact,
                                                                phone: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Téléphone <span className="obligatoire">*</span>
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'phone').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'phone')[0].message}</div>}
                                                        <div className="form-input">
                                                            <input type="text" className={errors.filter(e => e.path.join('-') === 'headCount').length > 0 ? 'requis' : ''} value={contact.headCount} onChange={(e) => setContact({
                                                                ...contact,
                                                                headCount: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Nombre d'élèves
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'headCount').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'headCount')[0].message}</div>}
                                                        <div className="form-input">
                                                            <input type="text" className={errors.filter(e => e.path.join('-') === 'level').length > 0 ? 'requis' : ''} value={contact.level} onChange={(e) => setContact({
                                                                ...contact,
                                                                level: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Niveau des élèves
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'level').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'level')[0].message}</div>}
                                                        <div className="form-input">
                                                            <textarea className={errors.filter(e => e.path.join('-') === 'message').length > 0 ? 'requis' : ''} rows={4} value={contact.message} onChange={(e) => setContact({
                                                                ...contact,
                                                                message: e.target.value
                                                            })} />
                                                            <label className="lh-1 text-16 text-light-1">
                                                                Message <span className="obligatoire">*</span>
                                                            </label>
                                                        </div>
                                                        {errors.filter(e => e.path.join('-') === 'message').length > 0 && <div className="f-requis">{errors.filter(e => e.path.join('-') === 'message')[0].message}</div>}
                                                        <div className="form-input justify-content-end pt-0">
                                                            <button className="button-primary d-block" onClick={() => postContact()}>
                                                                Envoyer
                                                            </button>
                                                        </div>
                                                    </>
                                                }
                                            </Collapse>
                                        </div>
                                    </div>
                                </div>
                                {/*<Link href={createStaticPagePathname('School', 'Quotation', { target: accomodation.id, targetType: 'Accomodation' })} className="button-primary devis d-block w-100">
                                    Demander un devis
                                </Link>*/}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}